import {
  Box,
  buildPath,
  buildTemplatePath,
  Button,
  Card,
  Grid,
  Page,
  PageRoute,
  pn,
  Stack,
  useElementBreakpoints,
  useValidationErrors,
  Typography,
} from "@compass/core";
import {
  Export,
  ExportGridData,
  ExportResource,
  LkExportSubTypeId,
  LkExportTypeId,
  StudentEnrollmentFilter as StudentEnrollmentFilterModel,
  StudentAbsenceExportFilter as StudentAbsenceFilterModel,
  StudentTestExportFilter as StudentTestFilterModel,
  StudentGpaExportFilter as StudentGpaFilterModel,
  StudentFilter as StudentFilterModel,
  StaffFilter as StaffFilterModel,
  StaffEmploymentFilter as StaffEmploymentFilterModel,
  StaffServiceFilter as StaffServiceFilterModel,
  PotentialStudentFilter as PotentialStudentFilterModel,
  StudentServiceFilter as StudentServiceFilterModel,
  NscCollegeFilter as NscCollegeFilterModel,
} from "generated/openApi";
import produce from "immer";
import { StudentEnrollmentFilter } from "pages/students/student-enrollments/StudentEnrollmentFilter";
import { StudentServiceFilter } from "pages/students/student-services/StudentServiceFilter";
import { StudentFilter } from "pages/students/students/StudentFilter";
import { ReactNode, useEffect, useState } from "react";
import ApiService from "services/apiService";
import { ExportDisplayFields } from "./ExportDisplayFields";
import { ExportGrid } from "./ExportGrid";
import { ExportPageHeader } from "./ExportPageHeader";
import { ExportType } from "./ExportType";
import { testExport } from "./test-export";
import { NscCollegeFilter } from "pages/nscColleges/NscCollegeFilter";
import { PotentialStudentFilter } from "pages/students/students/PotentialStudentFilter";
import { StaffFilter } from "pages/staff/staff/StaffFilter";
import { StaffServiceFilter } from "pages/staff/staff-services/StaffServiceFilter";
import { StudentAbsenceFilter } from "./StudentAbsenceFilter";

const routeName = "exports";

export const exportPageRoute: PageRoute = {
  handle: "Exports",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const ExportPage = () => {
  const { breakpointsRef, setBreakpoints, largeFieldBreakpoints } = useElementBreakpoints();
  const [screen, setScreen] = useState<"config" | "grid">("config");
  const [_export, setExport] = useState<Export>({} as Export);
  const [originalExport, setOriginalExport] = useState<Export>({} as Export);
  const [resource, setResource] = useState<ExportResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [gridData, setGridData] = useState<ExportGridData>();

  useEffect(() => {
    ApiService.exports.get().then((response) => {

      setExport(response);
      setOriginalExport(response);
    });

    ApiService.exports.getResource().then((response) => {
      setResource(response);
    });
  }, []);

  const filterTemplate = <T extends Object>(props: {
    header: ReactNode;
    filter: T;
    setFilter: (value: T) => void;
    resetFilter: T;
    children: ReactNode;
  }) => {
    return (
      <Card
        fullHeight
        header={
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="inherit">{props.header}</Typography>

            {/* {JSON.stringify(props.filter) !== JSON.stringify(props.resetFilter) && (
              <Button onClick={() => props.setFilter(props.resetFilter)}>Reset</Button>
            )} */}
          </Stack>
        }
        sx={{ height: "100%" }}
      >
        {props.children}
      </Card>
    );
  };

  const potentialStudentFilter = filterTemplate<PotentialStudentFilterModel>({
    header: "Potential Student Filter",
    filter: _export.potentialStudentFilter || ({} as PotentialStudentFilterModel),
    setFilter: (filter) => setExport({ ..._export, potentialStudentFilter: filter }),
    resetFilter: originalExport.potentialStudentFilter || ({} as PotentialStudentFilterModel),
    children: (
      <PotentialStudentFilter
        exportResource={resource}
        filter={_export.potentialStudentFilter || ({} as PotentialStudentFilterModel)}
        setFilter={(filter) => setExport({ ..._export, potentialStudentFilter: filter })}
      />
    ),
  });

  const staffFilter = filterTemplate<StaffFilterModel>({
    header: "Staff Filter",
    filter: _export.staffFilter || ({} as StaffFilterModel),
    setFilter: (filter) => setExport({ ..._export, staffFilter: filter }),
    resetFilter: originalExport.staffFilter || ({} as StaffFilterModel),
    children: (
      <StaffFilter
        filter={_export.staffFilter || ({} as StaffFilterModel)}
        setFilter={(filter) => setExport({ ..._export, staffFilter: filter })}
      />
    ),
  });

  const staffDisplayFields = (
    <ExportDisplayFields
      title="Staff"
      exportTypeId={_export.exportTypeId}
      displayFields={_export.staffDisplayFields || []}
      setDisplayFields={(displayFields) => setExport({ ..._export, staffDisplayFields: displayFields })}
      validationErrors={validationErrors[pn<Export>("staffDisplayFields")] || {}}
    />
  );

  // const staffEmploymentFilter = filterTemplate<StaffEmploymentFilterModel>({
  //   header: "Employment Filter",
  //   filter: _export.staffFilter || ({} as StaffFilterModel),
  //   setFilter: (filter) => setExport({ ..._export, staffEmploymentFilter: filter }),
  //   resetFilter: originalExport.staffFilter || {},
  //   children: (
  //     <StaffEmploymentFilter
  //       filter={_export.staffEmploymentFilter || {}}
  //       setFilter={(filter) => setExport({ ..._export, staffEmploymentFilter: filter })}
  //     />
  //   ),
  // });

  const staffEmploymentDisplayFields = (
    <ExportDisplayFields
      title="Employment"
      exportSubTypeId={_export.exportSubTypeId}
      displayFields={_export.staffEmploymentDisplayFields || []}
      setDisplayFields={(displayFields) =>
        setExport(
          produce((draft) => {
            draft.staffEmploymentDisplayFields = displayFields;
          })
        )
      }
      validationErrors={validationErrors[pn<Export>("staffEmploymentDisplayFields")] || {}}
    />
  );

  const staffServiceFilter = filterTemplate<StaffServiceFilterModel>({
    header: "Service Filter",
    filter: _export.staffServiceFilter || ({} as StaffServiceFilterModel),
    setFilter: (filter) => setExport({ ..._export, staffServiceFilter: filter }),
    resetFilter: originalExport.staffServiceFilter || {},
    children: (
      <StaffServiceFilter
        filter={_export.staffServiceFilter || {}}
        setFilter={(filter) => setExport({ ..._export, staffServiceFilter: filter })}
      />
    ),
  });

  const staffServiceDisplayFields = (
    <ExportDisplayFields
      title="Service"
      exportSubTypeId={_export.exportSubTypeId}
      displayFields={_export.staffServiceDisplayFields || []}
      setDisplayFields={(displayFields) => setExport({ ..._export, staffServiceDisplayFields: displayFields })}
      validationErrors={validationErrors[pn<Export>("staffServiceDisplayFields")] || {}}
    />
  );

  const studentFilter = filterTemplate<StudentFilterModel>({
    header: "Student Filter",
    filter: _export.studentFilter || ({} as StudentFilterModel),
    setFilter: (filter) => setExport({ ..._export, studentFilter: filter }),
    resetFilter: originalExport.studentFilter || ({} as StudentFilterModel),
    children: (
      <StudentFilter
        filter={_export.studentFilter || ({} as StudentFilterModel)}
        setFilter={(filter) => setExport({ ..._export, studentFilter: filter })}
      />
    ),
  });

  const studentDisplayFields = (
    <ExportDisplayFields
      title="Student"
      exportTypeId={_export.exportTypeId}
      displayFields={_export.studentDisplayFields || []}
      setDisplayFields={(displayFields) => setExport({ ..._export, studentDisplayFields: displayFields })}
      validationErrors={validationErrors[pn<Export>("studentDisplayFields")] || {}}
    />
  );

  const studentEnrollmentFilter = filterTemplate<StudentEnrollmentFilterModel>({
    header: "Enrollment Filter",
    filter: _export.studentFilter || ({} as StudentFilterModel),
    setFilter: (filter) => setExport({ ..._export, studentEnrollmentFilter: filter }),
    resetFilter: originalExport.studentFilter || {},
    children: (
      <StudentEnrollmentFilter
        filter={_export.studentEnrollmentFilter || {}}
        setFilter={(filter) => setExport({ ..._export, studentEnrollmentFilter: filter })}
      />
    ),
  });

  const studentAbsenceFilter = filterTemplate<StudentAbsenceFilterModel>({
    header: "Absence Filter",
    filter: _export.studentFilter || ({} as StudentFilterModel),
    setFilter: (filter) => setExport({ ..._export, studentAbsenceFilter: filter }),
    resetFilter: originalExport.studentFilter || {},
    children: (
      <StudentAbsenceFilter
        filter={_export.studentAbsenceFilter || {}}
        setFilter={(filter) => setExport({ ..._export, studentAbsenceFilter: filter })}
      />
    ),
  });

  const studentGpaFilter = filterTemplate<StudentGpaFilterModel>({
    header: "GPA Filter",
    filter: _export.studentFilter || ({} as StudentFilterModel),
    setFilter: (filter) => setExport({ ..._export, studentGpaFilter: filter }),
    resetFilter: originalExport.studentFilter || {},
    children: (
      <StudentAbsenceFilter
        filter={_export.studentGpaFilter || {}}
        setFilter={(filter) => setExport({ ..._export, studentGpaFilter: filter })}
      />
    ),
  });

  const studentTestFilter = filterTemplate<StudentTestFilterModel>({
    header: "Test Filter",
    filter: _export.studentFilter || ({} as StudentFilterModel),
    setFilter: (filter) => setExport({ ..._export, studentTestFilter: filter }),
    resetFilter: originalExport.studentFilter || {},
    children: (
      <StudentAbsenceFilter
        filter={_export.studentTestFilter || {}}
        setFilter={(filter) => setExport({ ..._export, studentTestFilter: filter })}
      />
    ),
  });

  const studentEnrollmentDisplayFields = (
    <ExportDisplayFields
      title="Enrollment"
      exportSubTypeId={_export.exportSubTypeId}
      displayFields={_export.studentEnrollmentDisplayFields || []}
      setDisplayFields={(displayFields) =>
        setExport(
          produce((draft) => {
            draft.studentEnrollmentDisplayFields = displayFields;
          })
        )
      }
      validationErrors={validationErrors[pn<Export>("studentEnrollmentDisplayFields")] || {}}
    />
  );

  const studentServiceFilter = filterTemplate<StudentServiceFilterModel>({
    header: "Service Filter",
    filter: _export.studentFilter || ({} as StudentFilterModel),
    setFilter: (filter) => setExport({ ..._export, studentServiceFilter: filter }),
    resetFilter: originalExport.studentFilter || {},
    children: (
      <StudentServiceFilter
        filter={_export.studentServiceFilter || {}}
        setFilter={(filter) => setExport({ ..._export, studentServiceFilter: filter })}
      />
    ),
  });

  const studentServiceDisplayFields = (
    <ExportDisplayFields
      title="Service"
      exportSubTypeId={_export.exportSubTypeId}
      displayFields={_export.studentServiceDisplayFields || []}
      setDisplayFields={(displayFields) => setExport({ ..._export, studentServiceDisplayFields: displayFields })}
      validationErrors={validationErrors[pn<Export>("studentServiceDisplayFields")] || {}}
    />
  );

  const studentFafsaDisplayFields = (
    <ExportDisplayFields
      title="FAFSA"
      exportSubTypeId={_export.exportSubTypeId}
      displayFields={_export.studentFafsaDisplayFields || []}
      setDisplayFields={(displayFields) =>
        setExport(
          produce((draft) => {
            draft.studentFafsaDisplayFields = displayFields;
          })
        )
      }
      validationErrors={validationErrors[pn<Export>("studentFafsaDisplayFields")] || {}}
    />
  );

  const studentGpaDisplayFields = (
    <ExportDisplayFields
      title="GPA"
      exportSubTypeId={_export.exportSubTypeId}
      displayFields={_export.studentGpaDisplayFields || []}
      setDisplayFields={(displayFields) =>
        setExport(
          produce((draft) => {
            draft.studentGpaDisplayFields = displayFields;
          })
        )
      }
      validationErrors={validationErrors[pn<Export>("studentGpaDisplayFields")] || {}}
    />
  );

  const studentAbsenceDisplayFields = (
    <ExportDisplayFields
      title="Absence"
      exportSubTypeId={_export.exportSubTypeId}
      displayFields={_export.studentAbsenceDisplayFields || []}
      setDisplayFields={(displayFields) =>
        setExport(
          produce((draft) => {
            draft.studentAbsenceDisplayFields = displayFields;
          })
        )
      }
      validationErrors={validationErrors[pn<Export>("studentAbsenceDisplayFields")] || {}}
    />
  );

  const studentTestDisplayFields = (
    <ExportDisplayFields
      title="Test"
      exportSubTypeId={_export.exportSubTypeId}
      displayFields={_export.studentTestDisplayFields || []}
      setDisplayFields={(displayFields) =>
        setExport(
          produce((draft) => {
            draft.studentTestDisplayFields = displayFields;
          })
        )
      }
      validationErrors={validationErrors[pn<Export>("studentTestDisplayFields")] || {}}
    />
  );

  const studentPostHSDisplayFields = (
    <ExportDisplayFields
      title="Post-HS"
      exportSubTypeId={_export.exportSubTypeId}
      displayFields={_export.studentPostHSDisplayFields || []}
      setDisplayFields={(displayFields) =>
        setExport(
          produce((draft) => {
            draft.studentPostHSDisplayFields = displayFields;
          })
        )
      }
      validationErrors={validationErrors[pn<Export>("studentPostHSDisplayFields")] || {}}
    />
  );

  const studentDiplomaDisplayFields = (
    <ExportDisplayFields
      title="Diploma"
      exportSubTypeId={_export.exportSubTypeId}
      displayFields={_export.studentDiplomaDisplayFields || []}
      setDisplayFields={(displayFields) =>
        setExport(
          produce((draft) => {
            draft.studentDiplomaDisplayFields = displayFields;
          })
        )
      }
      validationErrors={validationErrors[pn<Export>("studentDiplomaDisplayFields")] || {}}
    />
  );

  const studentParentDisplayFields = (
    <ExportDisplayFields
      title="Parent"
      exportSubTypeId={_export.exportSubTypeId}
      displayFields={_export.studentParentDisplayFields || []}
      setDisplayFields={(displayFields) =>
        setExport(
          produce((draft) => {
            draft.studentParentDisplayFields = displayFields;
          })
        )
      }
      validationErrors={validationErrors[pn<Export>("studentParentDisplayFields")] || {}}
    />
  );

  const nscCollegeFilter = filterTemplate<NscCollegeFilterModel>({
    header: "NSC Filter",
    filter: _export.nscCollegeFilter || ({} as NscCollegeFilterModel),
    setFilter: (filter) => setExport({ ..._export, nscCollegeFilter: filter }),
    resetFilter: originalExport.nscCollegeFilter || ({} as NscCollegeFilterModel),
    children: (
      <NscCollegeFilter
        filter={_export.nscCollegeFilter || ({} as NscCollegeFilterModel)}
        setFilter={(filter) => setExport({ ..._export, nscCollegeFilter: filter })}
      />
    ),
  });

  const nscCollegeDisplayFields = (
    <ExportDisplayFields
      title="NSC"
      exportTypeId={_export.exportTypeId}
      displayFields={_export.nscCollegeDisplayFields || []}
      setDisplayFields={(displayFields) => setExport({ ..._export, nscCollegeDisplayFields: displayFields })}
      validationErrors={validationErrors[pn<Export>("nscCollegeDisplayFields")] || {}}
    />
  );

  const potentialStudentDisplayFields = (
    <ExportDisplayFields
      title="Potential Student"
      exportTypeId={_export.exportTypeId}
      displayFields={_export.potentialStudentDisplayFields || []}
      setDisplayFields={(displayFields) => setExport({ ..._export, potentialStudentDisplayFields: displayFields })}
      validationErrors={validationErrors[pn<Export>("potentialStudentDisplayFields")] || {}}
    />
  );

  return (
    <Page
      header={
        <ExportPageHeader
          handle={exportPageRoute.handle}
          _export={_export}
          setGridData={setGridData}
          screen={screen}
          setScreen={setScreen}
          setValidationErrors={setValidationErrors}
        />
      }
    >
      <Box ref={breakpointsRef}>
        {/* export types, filters and display fields */}
        {screen === "config" && (
          <Stack spacing="lg">
            <ExportType
              _export={_export}
              setExport={setExport}
              resource={resource}
              validationErrors={validationErrors}
            />
            {_export?.exportTypeId && _export.exportTypeId > 0 && (
              <Stack spacing="lg">
                {_export?.exportTypeId === LkExportTypeId.Staff && (
                  <Box>
                    <Grid container spacing="lg">
                      <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {staffFilter}
                      </Grid>
                      <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {staffDisplayFields}
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {_export?.exportSubTypeId === LkExportSubTypeId.StaffEmployment && (
                  <Box>
                    <Grid container spacing="lg">
                      {/* <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {staffEmploymentFilter}
                      </Grid> */}

                      <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {staffEmploymentDisplayFields}
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {_export?.exportSubTypeId === LkExportSubTypeId.StaffService && (
                  <Box>
                    <Grid container spacing="lg">
                      <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {staffServiceFilter}
                      </Grid>

                      <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {staffServiceDisplayFields}
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {_export?.exportTypeId === LkExportTypeId.Student && (
                  <Box>
                    <Grid container spacing="lg">
                      <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {studentFilter}
                      </Grid>
                      <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {studentDisplayFields}
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {_export?.exportSubTypeId === LkExportSubTypeId.StudentEnrollment && (
                  <Box>
                    <Grid container spacing="lg">
                      <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {studentEnrollmentFilter}
                      </Grid>

                      <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {studentEnrollmentDisplayFields}
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {_export?.exportSubTypeId === LkExportSubTypeId.StudentService && (
                  <Box>
                    <Grid container spacing="lg">
                      <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {studentServiceFilter}
                      </Grid>

                      <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {studentServiceDisplayFields}
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {_export?.exportSubTypeId === LkExportSubTypeId.StudentParent && (
                  <Box>
                    <Grid container spacing="lg">
                      <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {studentParentDisplayFields}
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {_export?.exportSubTypeId === LkExportSubTypeId.StudentTest && (
                  <Box>
                    <Grid container spacing="lg">
                      <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {studentTestFilter}
                      </Grid>

                      <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {studentTestDisplayFields}
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {_export?.exportSubTypeId === LkExportSubTypeId.StudentGpa && (
                  <Box>
                    <Grid container spacing="lg">
                      <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {studentGpaFilter}
                      </Grid>

                      <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {studentGpaDisplayFields}
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {_export?.exportSubTypeId === LkExportSubTypeId.StudentAbsence && (
                  <Box>
                    <Grid container spacing="lg">
                      <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {studentAbsenceFilter}
                      </Grid>

                      <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {studentAbsenceDisplayFields}
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {_export?.exportSubTypeId === LkExportSubTypeId.StudentFafsa && (
                  <Box>
                    <Grid container spacing="lg">
                      <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {studentFafsaDisplayFields}
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {_export?.exportSubTypeId === LkExportSubTypeId.StudentPostHS && (
                  <Box>
                    <Grid container spacing="lg">
                      <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {studentPostHSDisplayFields}
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {_export?.exportSubTypeId === LkExportSubTypeId.StudentDiploma && (
                  <Box>
                    <Grid container spacing="lg">
                      <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {studentDiplomaDisplayFields}
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {_export?.exportTypeId === LkExportTypeId.NscCollege && (
                  <Box>
                    <Grid container spacing="lg">
                      <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {studentFilter}
                      </Grid>
                      <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {nscCollegeFilter}
                      </Grid>
                      <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {nscCollegeDisplayFields}
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {_export?.exportTypeId === LkExportTypeId.PotentialStudent && (
                  <Box>
                    <Grid container spacing="lg">
                      <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {potentialStudentFilter}
                      </Grid>
                      <Grid item xs={setBreakpoints({ lg: 6, xs: 12 })}>
                        {potentialStudentDisplayFields}
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Stack>
            )}

            {/* <pre>{JSON.stringify(_export, null, 4)}</pre> */}
          </Stack>
        )}

        {screen === "grid" && (
          <ExportGrid
            exportType={resource.exportTypes?.find((x) => x.id === _export?.exportTypeId)?.name}
            exportSubType={resource.exportSubTypes?.find((x) => x.id === _export?.exportSubTypeId)?.name}
            timezone={_export?.timezone}
            gridData={gridData || {}}
          />
        )}
      </Box>
    </Page>
  );
};
